<!--
Question

Renders the form to add/edit/delete questions.

As some of the questions can handle multiple entry fields which can't all be displayed in the text field at the same time,
they are displayed as list in card-based components (arrayAnswerCard).
-->
<template>
    <div>

        <!--Header-->
        <v-row class="ma-0 pa-4" no-gutters>
            <app-text category="text-default">New</app-text>
        </v-row>

        <!--Page Content-->
        <v-row no-gutters class="ma-0">
            <v-col xs="6" lg="12">

                <!--Tabs-->
                <v-tabs v-model="tabs" :height="buttonSizeDefault">

                    <!--Overview-->
                    <v-tab href="#overview">
                        <app-text category="text-small">Overview</app-text>
                    </v-tab>

                </v-tabs>

                <!--Tab Content-->
                <v-tabs-items v-model="tabs" class="appbackground">

                    <!--Overview Tab Content-->
                    <v-tab-item value="overview" class="d-flex flex-column pa-0">

                        <!--Title | Action buttons-->
                        <v-row class="pa-4" no-gutters>

                            <!--Title | Instructions-->
                            <v-col cols="12">

                                <!--Title-->
                                <app-text category="text-default-bold">Overview</app-text>

                                <!--Instructions-->
                                <app-text category="text-default">
                                    To add a new question, please fill out the form below.
                                    Some of the fields will accept multiple entries,
                                    which you can enter by checking the boxes in the fields.
                                </app-text>

                                <v-divider class="my-4"/>

                            </v-col>

                            <!--Action Buttons-->
                            <v-col class="d-flex">

                                <!--Delete-->
                                <v-btn v-if="formMode !== 'New'" @click.native="deleteDocument(form.id)"
                                       class="d-flex align-center mr-4" color="error" depressed
                                       :height="buttonSizeDefault">
                                    <v-icon class="icons8-trash" color="white"/>
                                    <app-text category="text-default" class="white--text ml-2">Delete</app-text>
                                </v-btn>

                                <!--Edit-->
                                <v-btn v-if="formMode !== 'New'" @click.native="editDocument()"
                                       class="d-flex align-center mr-4" color="warning" depressed
                                       :height="buttonSizeDefault">
                                    <v-icon class="icons8-edit-property" color="white"/>
                                    <app-text category="text-default" class="white--text ml-2">Edit</app-text>
                                </v-btn>

                                <v-spacer/>

                                <!--Cancel-->
                                <v-btn v-if="formMode === 'Edit'" @click.native="cancelDocument()"
                                       class="d-flex align-center mr-4" color="grey" depressed
                                       :height="buttonSizeDefault">
                                    <v-icon class="icons8-cancel" color="white"/>
                                    <app-text category="text-default" class="white--text ml-2">Cancel</app-text>
                                </v-btn>

                                <!--Save-->
                                <v-btn v-if="formMode === 'Edit' || formMode === 'New'" @click.native="validateForm"
                                       class="d-flex align-center" color="success" depressed
                                       :height="buttonSizeDefault">
                                    <v-icon class="icons8-save"/>
                                    <app-text category="text-default" class="ml-2">Save</app-text>
                                </v-btn>

                            </v-col>

                        </v-row>

                        <!--Form-->
                        <v-form>
                            <v-row class="px-4" no-gutters>

                                <!--Question Title-->
                                <v-col class="pa-1" cols="12">
                                    <v-text-field
                                        background-color="white"
                                        :disabled="formReadOnly"
                                        :error="errors.questionTitle"
                                        :error-messages="errors.questionTitle ? errors.questionTitleErrorMessage : ''"
                                        filled
                                        hide-details="auto"
                                        label="Title (optional)"
                                        required
                                        v-model="form.questionTitle"/>
                                </v-col>

                                <!--Question Name-->
                                <v-col class="pa-1" cols="12">
                                    <v-text-field
                                        background-color="white"
                                        :disabled="formReadOnly"
                                        :error="!formReadOnly ? errors.questionName : errors.questionName = false"
                                        :error-messages="errors.questionName ? errors.questionNameErrorMessage : ''"
                                        filled
                                        hide-details="auto"
                                        label="Question"
                                        required
                                        v-model="form.questionName"/>
                                </v-col>

                                <!--Question Description-->
                                <v-col class="pa-1" cols="12">
                                    <v-text-field
                                        background-color="white"
                                        :disabled="formReadOnly"
                                        :error="!formReadOnly ? errors.questionDescription : errors.questionDescription = false"
                                        :error-messages="errors.questionDescription ? errors.questionDescriptionErrorMessage : ''"
                                        filled
                                        hide-details="auto"
                                        label="Description (optional)"
                                        required
                                        v-model="form.questionDescription"/>
                                </v-col>

                                <!--Question Answers-->
                                <v-col class="d-flex pa-1" :cols="$vuetify.breakpoint.smAndDown ? 12 : 6">
                                    <v-select background-color="white"
                                              chips
                                              deletable-chips
                                              :disabled="formReadOnly"
                                              :error="!formReadOnly ? errors.questionAnswers : errors.questionAnswers = false"
                                              :error-messages="errors.questionAnswers ? errors.questionAnswersErrorMessage : ''"
                                              filled
                                              hide-details="auto"
                                              :items="answersOptionsData"
                                              label="Answers"
                                              multiple
                                              required
                                              small-chips
                                              v-model="form.questionAnswers"/>
                                </v-col>

                                <!--Question Correct Answers-->
                                <v-col class="d-flex pa-1" :cols="$vuetify.breakpoint.smAndDown ? 12 : 6">
                                    <v-select background-color="white"
                                              chips
                                              deletable-chips
                                              :disabled="formReadOnly"
                                              :error="!formReadOnly ? errors.questionCorrectAnswers : errors.questionCorrectAnswers = false"
                                              :error-messages="errors.questionCorrectAnswers ? errors.questionCorrectAnswersErrorMessage : ''"
                                              filled
                                              hide-details="auto"
                                              :items="correctAnswersOptionsData"
                                              label="Correct Answers"
                                              multiple
                                              required
                                              small-chips
                                              v-model="form.questionCorrectAnswers"/>
                                </v-col>

                                <!--Question Position-->
                                <v-col class="pa-1" :cols="$vuetify.breakpoint.smAndDown ? 12 : 6">
                                    <v-text-field
                                        background-color="white"
                                        :disabled="formReadOnly"
                                        :error="!formReadOnly ? errors.questionPosition : errors.questionPosition = false"
                                        :error-messages="errors.questionPosition ? errors.questionPositionErrorMessage : ''"
                                        filled
                                        hide-details="auto"
                                        label="Position (optional)"
                                        required
                                        v-model="form.questionPosition"/>
                                </v-col>

                                <!--Question Site IDs-->
                                <v-col class="d-flex pa-1" :cols="$vuetify.breakpoint.smAndDown ? 12 : 6">
                                    <v-select
                                        background-color="white"
                                        chips
                                        deletable-chips
                                        :disabled="formReadOnly"
                                        :error="!formReadOnly ? errors.questionSiteIDs : errors.questionSiteIDs = false"
                                        :error-messages="errors.questionSiteIDs ? errors.questionSiteIDsErrorMessage : ''"
                                        filled
                                        hide-details="auto"
                                        :items="sitesLookUp"
                                        item-text="siteName"
                                        label="Sites"
                                        multiple
                                        required
                                        return-object
                                        small-chips
                                        v-model="form.questionSiteIDs"/>
                                </v-col>

                                <!--Question User Category-->
                                <v-col class="d-flex pa-1" :cols="$vuetify.breakpoint.smAndDown ? 12 : 6">
                                    <v-select background-color="white"
                                              chips
                                              deletable-chips
                                              :disabled="formReadOnly"
                                              :error="!formReadOnly ? errors.questionUserCategories : errors.questionUserCategories = false"
                                              :error-messages="errors.questionUserCategories ? errors.questionUserCategoriesErrorMessage : ''"
                                              filled
                                              hide-details="auto"
                                              :items="userCategoriesOptionsData"
                                              label="User Categories"
                                              multiple
                                              required
                                              small-chips
                                              v-model="form.questionUserCategories"/>
                                </v-col>

                                <!--Question Types-->
                                <v-col class="pa-1" :cols="$vuetify.breakpoint.smAndDown ? 12 : 6">
                                    <v-select background-color="white"
                                              chips
                                              deletable-chips
                                              :disabled="formReadOnly"
                                              :error="!formReadOnly ? errors.questionTypes : errors.questionTypes = false"
                                              :error-messages="errors.questionTypes ? errors.questionTypesErrorMessage : ''"
                                              filled
                                              hide-details="auto"
                                              :items="questionTypesOptionsData"
                                              label="Question Types"
                                              multiple
                                              required
                                              small-chips
                                              v-model="form.questionTypes"/>
                                </v-col>

                            </v-row>
                        </v-form>

                    </v-tab-item>

                </v-tabs-items>

            </v-col>
        </v-row>

    </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import arrayAnswerCard from "../views/questions/arrayAnswerCard/arrayAnswerCard";

export default {
    name: "Questions",

    data: () => ({
        collection: 'questions',
        collectionTitle: 'Questions',
        collectionItem: 'question',
        collectionItemTitle: 'Question',
        errors: {
            questionTitle: false,
            questionTitleErrorMessage: "",
            questionName: false,
            questionNameErrorMessage: "",
            questionDescription: false,
            questionDescriptionErrorMessage: "",
            questionAnswers: false,
            questionAnswersErrorMessage: "",
            questionCorrectAnswers: false,
            questionCorrectAnswersMessage: "",
            questionPosition: false,
            questionPositionMessage: "",
            questionSiteIDs: false,
            questionSiteIDsMessage: "",
            questionUserCategories: false,
            questionUserCategoriesErrorMessage: "",
            questionTypes: false,
            questionTypesErrorMessage: ""
        },
        form: {
            id: '',
            questionTitle: '',
            questionName: '',
            questionDescription: '',
            questionAnswers: [],
            questionCorrectAnswers: [],
            questionPosition: '',
            questionSiteIDs: [],
            questionUserCategories: [],
            questionTypes: []
        },
        formMode: 'New',
        formReadOnly: false,
        questionAnswers: [],
        questionCorrectAnswers: [],
        questionSites: [],
        questionUserCategories: [],
        sitesLookUp: [],
        tabs: null,
    }),

    components: {
        arrayAnswerCard,
    },

    computed: ({
        ...mapGetters({
            GET_openItem: 'GET_openQuestion',
            GET_sites: 'GET_sites',
            GET_lookupValues: 'GET_lookupValues',
            GET_confirmBoxResult: 'confirmBox_store/GET_confirmBoxResult',
        })
    }),

    methods: {
        ...mapActions({ACT_openItem: "ACT_openQuestion", ACT_openQuestion: "ACT_openQuestion"}),

        /**
         * Get Sites Collection Data
         *
         * Fetch the data from the collection.
         * Iterate over the collection and push documents not marked as deleted.
         */
        getSitesCollectionData() {
            const t = this

            const collection = t.$firebase.db.collection('sites')
            collection.onSnapshot(snapshot => {

                // Clear the data to avoid duplicates
                t.sitesLookup = []

                snapshot.forEach(doc => {

                    const document = doc.data()
                    document.id = doc.id

                    // Only add documents that aren't marked as deleted
                    if (!document.hasOwnProperty('delete')) {
                        t.sitesLookUp.push(document)
                    }

                })
            })
        },

        /**
         * Validate
         *
         * Validates the required fields for presence only.
         * If any of the fields are missing mark them in an errors object.
         * When there are no errors left, save the record.
         */
        validateForm() {
            const t = this

            t.errors.questionTitle = false
            t.errors.questionName = false
            t.errors.questionDescription = false
            t.errors.questionAnswers = false
            t.errors.questionCorrectAnswers = false
            t.errors.questionPosition = false
            t.errors.questionSiteIDs = false
            t.errors.questionUserCategories = false
            t.errors.questionTypes = false

            // OPTIONAL - If present, Question title must be longer than 2 characters
            if (!t.form.questionTitle) {
                t.errors.questionTitle = false
            } else if (t.form.questionTitle.trim().length < 2) {
                t.errors.questionTitle = true
                t.errors.questionTitleErrorMessage = 'Question Title must be longer than 2 characters'
            } else {
                t.form.questionTitle = t.form.questionTitle.trim()
            }

            // REQUIRED - Question Name must be present and longer than 2 characters
            if (!t.form.questionName) {
                t.errors.questionName = true
                t.errors.questionNameErrorMessage = 'Question Name is required.'
            } else if (t.form.questionName.trim().length < 2) {
                t.errors.questionName = true
                t.errors.questionNameErrorMessage = 'Question Name must be longer than 2 characters'
            } else {
                t.form.questionName = t.form.questionName.trim()
            }
            
            // OPTIONAL - If present, Question Description must be longer than 2 characters
            if (!t.form.questionDescription) {
                t.errors.questionDescription = false
            } else if (t.form.questionDescription.trim().length < 2){
                t.errors.questionDescription = true
                t.errors.questionDescriptionErrorMessage = 'Question Description must be longer than 2 characters'
            } else {                
                t.form.questionDescription = t.form.questionDescription.trim()
            }

            // REQUIRED - Select an answer
            if (!t.form.questionAnswers.length) {
                t.errors.questionAnswers = true
                t.errors.questionAnswersErrorMessage = 'Please select at least 1 answer'
            } 

            // REQUIRED - Select a correct answer
            if (!t.form.questionCorrectAnswers.length) {
                t.errors.questionCorrectAnswers = true
                t.errors.questionCorrectAnswersErrorMessage = 'Please select at least 1 answer'
            } 

            // OPTIONAL - input a position
            if (!t.form.questionPosition) {
                t.errors.questionPosition = false
            } else if (t.form.questionPosition.trim().length < 1) {
                t.errors.questionPosition = true
                t.errors.questionPositionErrorMessage = 'Question Position must be longer than 1 character'
            } else {
                t.form.questionPosition = t.form.questionPosition.trim()
            }

            // REQUIRED - Site
            if (!t.form.questionSiteIDs.length) {
                t.errors.questionSiteIDs = true
                t.errors.questionSiteIDsErrorMessage = 'Site required'
            }

            // REQUIRED - User categories
            if (!t.form.questionUserCategories.length) {
                t.errors.questionUserCategories = true
                t.errors.questionUserCategoriesErrorMessage = 'User Category required'
            }

            // REQUIRED - Question Types
            if (!t.form.questionTypes.length) {
                t.errors.questionTypes = true
                t.errors.questionTypesErrorMessage = 'Question Types required'
            }

            // Check if there any errors left
            if (!Object.values(t.errors).includes(true)) {
                this.saveDocument()
            }
        },

        /**
         * Save Questions
         *
         * Save or update the document with the collection name and form details.
         *
         * @returns {Promise<void>}
         */
        async saveDocument() {
            const t = this
            let createDocumentResult

            // If the formMode is New, create a new document
            if (t.formMode === 'New') {
                createDocumentResult = await t.MIX_createDocument(t.collection, t.form)
            }

            // If the formMode is Edit, update the current document
            else if (t.formMode === 'Edit') {
                createDocumentResult = await t.MIX_updateDocument(t.collection, t.form)
            }

            // If the document saved/updated successfully, set the form back to viewable state
            if (createDocumentResult.code === 1) {
                t.formMode = 'View'
                t.formReadOnly = true
                await t.refreshDocumentView();
            }

            // Call for a confirmation alert
            t.renderConfirmationAlert(createDocumentResult)
        },

        /**
         * Edit Document
         *
         * Set the form to be in an editable state where it can be saved.
         */
        editDocument() {
            const t = this

            t.formMode = 'Edit'
            t.formReadOnly = false
        },

        /**
         * Delete Document
         *
         * Call for the confirmation box to delete a document.
         * The deletion is handled in this file's watch -> GET_confirmBoxResult handler function.
         */
        deleteDocument: function (id) {
            const t = this
            const width = t.$vuetify.breakpoint.width <= 425 ? '' : '400'

            t.MIX_confirmBox(
                {
                    show: true,
                    collection: t.collection,
                    id: id,
                    title: `Delete ${t.collectionItemTitle}`,
                    description: `Are you sure you want to delete this ${t.collectionItemTitle}: <br /><div class="mt-4"><strong>${t.form[t.collectionItem + 'Name']}</strong></div>`,
                    width,
                    trueButtonText: 'Yes',
                    trueButtonValue: 'YES',
                    falseButtonText: 'No',
                    falseButtonValue: 'NO',
                }
            )
        },

        /**
         * Cancel Document
         *
         * Set the form back to a readable state and refresh the data.
         */
        cancelDocument() {
            const t = this
            t.formMode = 'View'
            t.formReadOnly = true
            t.refreshDocumentView();
            t.closeRightPanel();
        },

        /**
         * Refresh Document View
         *
         * Load the new document and sets the payload onto ACT_openItem
         */
        async refreshDocumentView() {
            const t = this;

            const itemResult = await t.MIX_readDocumentById(t.collection, t.form.id)
            let payload = {}
            payload.process = 'View';
            payload.popup = false;
            payload.data = itemResult.data;

            await t.ACT_openItem(payload);
        },

        /**
         * Render Confirmation Alert
         *
         * Take the response of creating a document and render:
         *  -> 'Success' alert if all is ok
         *  -> 'Error' alert if something when wrong
         */
        renderConfirmationAlert(document) {
            const t = this

            if (document.code === 1) {
                t.MIX_alert(1, `${t.collectionItemTitle} saved`, null, null)
            } else {
                t.MIX_alert(-1, `Error saving ${t.collectionItemTitle}`, null, null)
            }
        }
    },

    watch: {
        GET_confirmBoxResult: {
            handler: async function () {
                const t = this

                if (t.GET_confirmBoxResult.data.collection === 'questions') {
                    if (t.GET_confirmBoxResult.data.result === 'YES') {
                        const deleteDocumentResult = await t.MIX_deleteDocument('questions', t.GET_confirmBoxResult.data.id);

                        if (deleteDocumentResult.code === 1) {
                            t.MIX_alert(1, `Question Deleted`, deleteDocumentResult.data, null);
                            await t.refreshDocumentView();
                        } else {
                            t.MIX_alert(-1, `Error Deleting Question`, null, null);
                        }
                    }
                } else if (t.GET_confirmBoxResult.data.collection === 'questions/destroy') {
                    if (t.GET_confirmBoxResult.data.result === 'YES') {
                        const destroyDocumentResult = await t.MIX_destroyDocument('questions', t.GET_confirmBoxResult.data.id);

                        if (destroyDocumentResult.code === 1) {
                            t.MIX_alert(1, `Question Destroyed`, destroyDocumentResult.data, null);
                            t.MIX_setPanelLayout(12, 0, false, '', '');
                        } else {
                            t.MIX_alert(-1, `Error Destroying Question`, null, null);
                        }
                    }
                }

            }, deep: true
        }
    },

    /**
     * Mounted
     *
     * Pre-load the required data before components render to the screen.
     *
     * @returns {Promise<void>}
     */
    async mounted() {
        const t = this

        // Get required  collection data
        await t.getSitesCollectionData()

        // Get and populate form data
        const openItem = await t.GET_openItem
        t.formMode = openItem.process;
        if (openItem.process === 'New') {
            t.formReadOnly = false
        } else {
            t.form = openItem.data
            t.formReadOnly = true
        }

        // if (openItem.popup === false) {
        // 	t.updateBreadcrumbs()
        // }
    }
}
</script>

<style>
.icon-round-right {
    background: white;
    border-radius: 0 50% 50% 0;
}
</style>
